import localforage from 'localforage'
import * as Sentry from '@sentry/vue'
import SlideApi from '@/api/slide'
import { getObjectFromIndexedDB, Stores } from '@/utils/indexedDB/indexed.db'
import renderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'

const SET_SELECTING_CATEGORY = 'SET_SELECTING_CATEGORY'

const GET_PUBLIC_SLIDE_TEMPLATES_START = 'GET_PUBLIC_SLIDE_TEMPLATES_START'
const GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED = 'GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED'
const GET_PUBLIC_SLIDE_TEMPLATES_FAILED = 'GET_PUBLIC_SLIDE_TEMPLATES_FAILED'

const templateCategoryEmojiMap = {
  Work: '💼',
  School: '🏫',
  Fun: '🎉',
  Holidays: '🎄',
  Others: '⭐️',
}

const getCategoryFromName = (name) => {
  if (!name) return null
  name = name.trim()
  return {
    name,
    emoji: templateCategoryEmojiMap[name] || templateCategoryEmojiMap.Others,
  }
}

const getPublicSlideTemplatesCache = async () => {
  const cachedTemplateIds = await localforage.getItem('content_slide_templates_ids')
  if (!cachedTemplateIds || cachedTemplateIds.length === 0) return null
  const promises = cachedTemplateIds.map(async (id) => {
    const cachedTemplate = await getObjectFromIndexedDB(id, Stores.contentSlideCategoriesStore)
    if (!cachedTemplate) return null
    if (cachedTemplate.thumbnailCache) {
      const url = URL.createObjectURL(cachedTemplate.thumbnailCache)
      cachedTemplate.backgroundCache = url
    }

    if (cachedTemplate.canvasBlocksCache) {
      cachedTemplate.canvasBlocks = { ...cachedTemplate.canvasBlocksCache }
      Object.keys(cachedTemplate.canvasBlocks).forEach((blockId) => {
        const block = cachedTemplate.canvasBlocks[blockId]
        if (block.type === 'Image' && block.content.blob) {
          const url = URL.createObjectURL(block.content.blob)
          cachedTemplate.canvasBlocks[blockId].content.srcCache = url
        }
      })
    }
    return cachedTemplate
  })
  const data = await Promise.all(promises)
  if (!data || data.length === 0) return null
  return data.filter((item) => !!item) // filter out null item
}

// const setPublicSlideTemplatesCache = async (templates) => {
//   // cache template locally for 4h, because the templates are not updated regularly
//   if (!templates || templates.length === 0) return
//   await localforage.setItem('content_slide_templates_ids', templates.map((i) => i.id))
//   const promises = templates.map(async (template) => {
//     const cachedTemplate = { ...template }

//     if (template.backgroundImage) {
//       const thumbnailImageUrl = template.backgroundImage.thumbnail
//       const thumbnailBlob = await fetch(thumbnailImageUrl).then((r) => r.blob())
//       cachedTemplate.thumbnailCache = thumbnailBlob
//     }

//     if (template.canvasBlocks && Object.keys(template.canvasBlocks)) {
//       cachedTemplate.canvasBlocksCache = { ...template.canvasBlocks }
//       const imageCachePromises = Object.keys(cachedTemplate.canvasBlocksCache)
//         .map(async (blockId) => {
//           const block = cachedTemplate.canvasBlocksCache[blockId]
//           if (block.type === 'Image' && block.content.src) {
//             return fetch(block.content.src)
//               .then((r) => r.blob())
//               .catch(() => {
//                 return null
//               })
//           }
//           return Promise.resolve(null)
//         })

//       const imageCaches = await Promise.all(imageCachePromises)

//       Object.keys(cachedTemplate.canvasBlocksCache).forEach((blockId, index) => {
//         if (imageCaches[index]) {
//           cachedTemplate.canvasBlocksCache[blockId].content.blob = imageCaches[index]
//         }
//       })

//       delete cachedTemplate.canvasBlocks
//     }
//     return cachedTemplate
//   })
//   const data = await Promise.all(promises)
//   data.forEach((template) => {
//     setObjectToIndexedDB(template.id, Stores.contentSlideCategoriesStore, template)
//   })
// }

const _state = {
  publicSlideTemplates: null,
  isLoadingPublicSlideTemplates: false,
  getPublicSlideTemplateError: null,
  selectingCategory: null,
}

const getters = {
  publicSlideTemplates(state) {
    return state.publicSlideTemplates
  },
  categorySlideTemplates(state) {
    if (!state.publicSlideTemplates ||
      state.publicSlideTemplates.length === 0 ||
      !state.selectingCategory
    ) {
      return []
    }

    return state.publicSlideTemplates.filter((item) => item.title === state.selectingCategory.name)
  },
  selectingCategory(state) {
    return state.selectingCategory
  },
  contentSlideTemplateCategories() {
    return Object.keys(templateCategoryEmojiMap).map((item) => {
      return {
        name: item,
        emoji: templateCategoryEmojiMap[item],
      }
    })
  },
  isLoadingPublicSlideTemplates(state) {
    return state.isLoadingPublicSlideTemplates
  },
  getPublicSlideTemplateError(state) {
    return state.getPublicSlideTemplateError
  },
}

const actions = {
  loadContentSlideTemplatesCacheAction: async ({ commit }) => {
    const templates = await getPublicSlideTemplatesCache()
    commit(GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED, templates)
  },

  getLatestPublicSlideTemplateAction: async ({ commit }, options = {}) => {
    const { cacheOnly = false } = options

    if (!cacheOnly) {
      commit(GET_PUBLIC_SLIDE_TEMPLATES_START)
    }
    try {
      const templates = await SlideApi.getPublicSlideTemplates()

      const fetchBlocksPromises = templates.map(async (template) => {
        return SlideApi.getCanvasBlocks(template)
      })

      const resp = await Promise.all(fetchBlocksPromises)

      templates.forEach(async (template, index) => {
        if (!template.title) {
          template.title = 'Others'
        }
        const originalBlocks = resp[index].body
        const {
          canvasBlocksWithNewSignedUrls,
        } = await renderCanvasBlocksUtils.resignContentSlideImageUrlsIfExpired(originalBlocks)
        template.canvasBlocks = canvasBlocksWithNewSignedUrls
      })

      templates.sort((a, b) => {
        return a.order - b.order
      })

      if (!cacheOnly) {
        commit(GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED, templates)
        commit(SET_SELECTING_CATEGORY, getCategoryFromName(templates[0].title))
      }

      // setPublicSlideTemplatesCache(templates)
    } catch (error) {
      Sentry.captureException(error)
      if (!cacheOnly) {
        commit(GET_PUBLIC_SLIDE_TEMPLATES_FAILED, error)
      }
    }
  },

  getPublicSlideTemplatesAction: async ({ commit, state, dispatch }) => {
    // if (!state.publicSlideTemplates) {
    //   await dispatch('loadContentSlideTemplatesCacheAction')
    // }
    // If template is still in the cache, then do nothing
    if (state.publicSlideTemplates && state.publicSlideTemplates.length > 0) {
      if (!state.selectingCategory) {
        const firstIdx = state.publicSlideTemplates.findIndex((item) => item.title)
        if (firstIdx > -1) {
          commit(
            SET_SELECTING_CATEGORY,
            getCategoryFromName(state.publicSlideTemplates[firstIdx].title),
          )
        }
      }
      // fetch the new template in the background
      await dispatch('getLatestPublicSlideTemplateAction', { cacheOnly: true })
      return
    }

    await dispatch('getLatestPublicSlideTemplateAction', { cacheOnly: false })
  },
  clearPublicSlideTemplateAction: ({ commit }) => {
    commit(GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED, null)
  },
  selectContentSlideCategoryAction: ({ commit }, category) => {
    commit(SET_SELECTING_CATEGORY, category)
  },
}

const mutations = {
  [GET_PUBLIC_SLIDE_TEMPLATES_START](state) {
    state.isLoadingPublicSlideTemplates = true
  },
  [GET_PUBLIC_SLIDE_TEMPLATES_SUCCEEDED](state, templates) {
    state.publicSlideTemplates = templates
    state.isLoadingPublicSlideTemplates = false
  },
  [SET_SELECTING_CATEGORY](state, category) {
    state.selectingCategory = category
  },
  [GET_PUBLIC_SLIDE_TEMPLATES_FAILED](state, error) {
    state.getPublicSlideTemplateError = error
    state.isLoadingPublicSlideTemplates = false
  },
}

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
